import {
  REVENUE_LEDGER_BREAKDOWN_LABELS,
  REVENUE_LEDGER_PLAN_LABELS,
  type RevenueDeferredStatus,
  type RevenueLedgerMonthlyResponse,
  type RevenueLedgerRow,
} from '@cortex/domain-ac-mens-hub-api';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranslation } from 'react-i18next';

/** @graph-connects none */
type StatusKey = keyof RevenueDeferredStatus;

/**
 * 前受金の状態別の内訳の列。labelKey は描画側で t() する翻訳キー。
 * @graph-connects none
 */
const STATUS_COLUMNS: readonly { key: StatusKey; labelKey: string }[] = [
  { key: 'confirmed', labelKey: 'revenue.ledger.statusConfirmed' },
  { key: 'pending', labelKey: 'revenue.ledger.statusPending' },
  { key: 'noEvidence', labelKey: 'revenue.ledger.statusNoEvidence' },
  { key: 'withdrawn', labelKey: 'revenue.ledger.statusWithdrawn' },
];

/** 前受金の列にするチケットの種類。 @graph-connects none */
const DEFERRED_BREAKDOWNS = ['membership_ticket', 'purchased_ticket'] as const;

/** 貸倒の列にするプランと内訳。 @graph-connects none */
const BAD_DEBT_COLUMNS = (['monthly', 'bimonthly'] as const).flatMap((plan) =>
  (['balance', 'increase', 'decrease'] as const).map((breakdown) => ({ plan, breakdown })),
);

/** @graph-connects none */
function cellOf(
  rows: readonly RevenueLedgerRow[],
  match: (r: RevenueLedgerRow) => boolean,
  month: string,
) {
  return rows.filter(match).flatMap((r) => r.cells.filter((c) => c.month === month));
}

/** @graph-connects none */
function yen(value: number | null): string {
  return value === null ? '–' : value.toLocaleString('ja-JP');
}

/** @graph-connects none */
function sumOrNull(values: readonly number[]): number | null {
  return values.length === 0 ? null : values.reduce((a, b) => a + b, 0);
}

/** @graph-connects none */
const TH =
  'border-b border-[var(--color-border)] px-3 py-2 text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]';
/** @graph-connects none */
const TD =
  'border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]';

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 売上集計（正式版）の前受金・貸倒タブ。前受金を全額と状態別の内訳（確定・確定待ち・証跡なし・うち退会済み）で、貸倒を1ヶ月に1回・2ヶ月に1回の残高と月次の増減で、月ごとに並べる。
 * @graph-connects none
 */
export function LedgerBalanceView({ ledger }: { ledger: RevenueLedgerMonthlyResponse }) {
  const { t } = useTranslation();
  const deferredRows = ledger.rows.filter((r) => r.bucket === 'deferred_revenue');
  const badDebtRows = ledger.rows.filter((r) => r.bucket === 'bad_debt');

  return (
    <div className="mt-6 space-y-6">
      <p className="text-xs text-[var(--color-text-secondary)]">
        {t('revenue.ledger.balanceNote')}
      </p>
      <Card className="overflow-x-auto p-4">
        <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          {t('revenue.ledger.deferredTitle')}
        </h3>
        <table className="min-w-full border-collapse text-right text-sm">
          <thead>
            <tr>
              <th className={`${TH} text-left`}>{t('revenue.ledger.month')}</th>
              {DEFERRED_BREAKDOWNS.map((b) => (
                <th key={b} className={TH}>
                  {REVENUE_LEDGER_BREAKDOWN_LABELS[b]}
                </th>
              ))}
              <th className={`${TH} font-semibold`}>{t('revenue.ledger.deferredTotal')}</th>
              {STATUS_COLUMNS.map((s) => (
                <th key={s.key} className={TH}>
                  {t(s.labelKey)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledger.months.map((month) => {
              const cells = cellOf(deferredRows, () => true, month);
              return (
                <tr key={month}>
                  <td className={`${TD} text-left font-mono`}>{month}</td>
                  {DEFERRED_BREAKDOWNS.map((b) => (
                    <td key={b} className={TD}>
                      {yen(
                        sumOrNull(
                          cellOf(deferredRows, (r) => r.breakdown === b, month).map(
                            (c) => c.amountNet,
                          ),
                        ),
                      )}
                    </td>
                  ))}
                  <td className={`${TD} font-semibold`}>
                    {yen(sumOrNull(cells.map((c) => c.amountNet)))}
                  </td>
                  {STATUS_COLUMNS.map((s) => (
                    <td key={s.key} className={TD}>
                      {yen(
                        sumOrNull(
                          cells.flatMap((c) => {
                            const v = c.deferredStatus?.[s.key];
                            return v === null || v === undefined ? [] : [v];
                          }),
                        ),
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card className="overflow-x-auto p-4">
        <h3 className="mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          {t('revenue.ledger.badDebtTitle')}
        </h3>
        <table className="min-w-full border-collapse text-right text-sm">
          <thead>
            <tr>
              <th className={`${TH} text-left`}>{t('revenue.ledger.month')}</th>
              {BAD_DEBT_COLUMNS.map(({ plan, breakdown }) => (
                <th key={`${plan}/${breakdown}`} className={TH}>
                  {`${REVENUE_LEDGER_PLAN_LABELS[plan]}・${REVENUE_LEDGER_BREAKDOWN_LABELS[breakdown]}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ledger.months.map((month) => (
              <tr key={month}>
                <td className={`${TD} text-left font-mono`}>{month}</td>
                {BAD_DEBT_COLUMNS.map(({ plan, breakdown }) => (
                  <td key={`${plan}/${breakdown}`} className={TD}>
                    {yen(
                      sumOrNull(
                        cellOf(
                          badDebtRows,
                          (r) => r.planCode === plan && r.breakdown === breakdown,
                          month,
                        ).map((c) => c.amountNet),
                      ),
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
